import React from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import SophisticatedTooltipLink from '@wix/da-ds/pkg/tooltips/SophisticatedTooltipLink';
import { useSubscriptionTier } from '../../../utils/hooks/useSubscriptionTier';
import SophisticatedTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SophisticatedTooltip';
import { getRelatedContentBlock } from '../../../DeviationViews/_helpers';

const TOOLTIP_WIDTH_PX = 256;

export interface Props {
  deviation: PapiDeviation;
  linkClassName?: string;
}

/** The owner's SubscribersOnly tooltip contains info about how many tiers
 *  contain the deviation and those tier titles that isn't used in the other tooltips. */
const SubscribersOnlyOwnersTooltip: React.FC<React.PropsWithChildren<Props>> = ({
  deviation,
  linkClassName,
  children,
}) => {
  const { t } = useTranslation();
  const { tier, isTier, isOwner, subscriptionsTabUrl } = useSubscriptionTier(deviation);

  // no tooltip for tier deviation viewed by owner
  if (!tier || !isOwner || isTier) {
    return <>{children}</>;
  }

  // when the owner is viewing an in-tier, we show a list of the tiers
  // containing this deviation in the description and a count in the heading
  const inTiers = getRelatedContentBlock(deviation, 'in_tiers')?.deviations ?? [];
  return (
    <SophisticatedTooltip
      size="large"
      placement="top"
      width={TOOLTIP_WIDTH_PX}
      heading={t('deviation.tier.subscribersOnly.tooltip.owner.tierCount', {
        // note: the inTiers list includes the current tier
        count: inTiers.length,
      })}
      description={inTiers.map(otherTier => otherTier.title).join('\n')}
      postfix={
        <SophisticatedTooltipLink href={subscriptionsTabUrl} className={linkClassName}>
          {t('deviation.tier.subscribersOnly.tooltip.subscriptionsLinkOwner')}
        </SophisticatedTooltipLink>
      }
    >
      {children}
    </SophisticatedTooltip>
  );
};
SubscribersOnlyOwnersTooltip.displayName = 'SubscribersOnlyOwnersTooltip';
export default SubscribersOnlyOwnersTooltip;
